import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "languageSelect", "languageToggle", "languageColumn"]
  static values = { redirectUrl: String }

  declare readonly formTarget: HTMLFormElement
  declare readonly languageSelectTarget: HTMLSelectElement
  declare readonly languageToggleTargets: HTMLElement[]
  declare readonly languageColumnTargets: HTMLElement[]
  declare readonly redirectUrlValue: string

  connect() {
    this.updateSubmitButtonState()
  }

  changeLanguage(event: Event) {
    const element = event.target as HTMLElement
    const selectedLanguage = element.closest('[data-language]')?.getAttribute('data-language') || (element as HTMLSelectElement).value
    const currentUrl = new URL(window.location.href)
    
    // Update the language parameter in the URL
    currentUrl.searchParams.set('language', selectedLanguage)
    // Remove edit_all parameter when switching languages
    currentUrl.searchParams.delete('edit_all')
    
    // Reload the page with the new language
    window.location.href = currentUrl.toString()
  }

  toggleLanguage(event: Event) {
    const button = event.target as HTMLElement
    const language = button.closest('[data-language]')?.getAttribute('data-language')
    if (!language) return

    // Always show only the clicked language
    this.showOnlyLanguage(language)
  }

  showAllLanguages() {
    this.languageToggleTargets.forEach((button: HTMLElement) => {
      if (button.getAttribute('data-visible') !== 'true') {
        button.setAttribute('data-visible', 'true')
        button.className = "inline-flex items-center px-3 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-medium rounded-lg transition-colors"
        
        const icon = button.querySelector('svg')
        if (icon) {
          icon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>'
        }
      }
    })

    this.languageColumnTargets.forEach((column: HTMLElement) => {
      column.style.display = ''
    })

    // Update submit button state
    this.updateSubmitButtonState()
  }

  hideAllLanguages() {
    this.languageToggleTargets.forEach((button: HTMLElement) => {
      if (button.getAttribute('data-visible') !== 'false') {
        button.setAttribute('data-visible', 'false')
        button.className = "inline-flex items-center px-3 py-2 bg-red-100 hover:bg-red-200 text-red-800 font-medium rounded-lg transition-colors"
        
        const icon = button.querySelector('svg')
        if (icon) {
          icon.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>'
        }
      }
    })

    this.languageColumnTargets.forEach((column: HTMLElement) => {
      column.style.display = 'none'
    })

    // Update submit button state
    this.updateSubmitButtonState()
  }

  showOnlyLanguage(language: string) {
    this.languageToggleTargets.forEach((button: HTMLElement) => {
      const buttonLanguage = button.getAttribute('data-language')
      const shouldBeVisible = buttonLanguage === language

      button.setAttribute('data-visible', shouldBeVisible.toString())
      button.className = shouldBeVisible
        ? "inline-flex items-center px-3 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-medium rounded-lg transition-colors"
        : "inline-flex items-center px-3 py-2 bg-red-100 hover:bg-red-200 text-red-800 font-medium rounded-lg transition-colors"
      
      const icon = button.querySelector('svg')
      if (icon) {
        icon.innerHTML = shouldBeVisible
          ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>'
          : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"></path>'
      }
    })

    this.languageColumnTargets.forEach((column: HTMLElement) => {
      const columnLanguage = column.getAttribute('data-language')
      column.style.display = columnLanguage === language ? '' : 'none'
    })

    // Update submit button state
    this.updateSubmitButtonState()
  }

  submitForm(event: Event) {
    event.preventDefault()

    const form = this.formTarget as HTMLFormElement
    const formData = new FormData()

    // Check if we're in edit_all mode (multi-language table) or single-language mode
    const isEditAllMode = this.languageColumnTargets.length > 0

    let inputsToInclude: NodeListOf<HTMLInputElement>

    if (isEditAllMode) {
      // Multi-language mode: only include inputs from visible language columns
      const visibleColumns = this.languageColumnTargets.filter((column: HTMLElement) => 
        column.style.display !== 'none'
      )
      
      const visibleLanguages = visibleColumns.map((column: HTMLElement) => 
        column.getAttribute('data-language')
      ).filter((lang: string | null): lang is string => Boolean(lang))

      // Collect inputs only from visible language columns
      inputsToInclude = form.querySelectorAll('input[type="text"][name]') as NodeListOf<HTMLInputElement>
      // Filter to only include inputs from visible languages
      const filteredInputs: HTMLInputElement[] = []
      inputsToInclude.forEach((input: HTMLInputElement) => {
        const inputLanguage = input.getAttribute('data-language')
        if (inputLanguage && visibleLanguages.includes(inputLanguage)) {
          filteredInputs.push(input)
        }
      })
      inputsToInclude = {
        length: filteredInputs.length,
        forEach: (callback: (input: HTMLInputElement) => void) => filteredInputs.forEach(callback),
        [Symbol.iterator]: () => filteredInputs[Symbol.iterator]()
      } as NodeListOf<HTMLInputElement>
    } else {
      // Single-language mode: include all text inputs
      inputsToInclude = form.querySelectorAll('input[type="text"][name]') as NodeListOf<HTMLInputElement>
    }

    // Add CSRF token
    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
    if (csrfToken) {
      formData.append('authenticity_token', csrfToken)
    }

    // Collect the inputs
    inputsToInclude.forEach((input: HTMLInputElement) => {
      if (input.name && input.value !== undefined) {
        formData.append(input.name, input.value)
      }
    })

    // Also include hidden inputs
    const hiddenInputs = form.querySelectorAll('input[type="hidden"]') as NodeListOf<HTMLInputElement>
    hiddenInputs.forEach((input: HTMLInputElement) => {
      if (input.name && input.value !== undefined) {
        formData.append(input.name, input.value)
      }
    })

    // Show loading state
    const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement
    const originalText = submitButton.textContent
    submitButton.textContent = "Saving..."
    submitButton.disabled = true

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': csrfToken || ''
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Show success message
        this.showNotification(`Successfully updated ${data.results.filter((r: any) => r.success).length} phrases`, 'success')
        // Redirect back to phrases files index after a short delay
        setTimeout(() => {
          window.location.href = this.redirectUrlValue || '/admin/phrases'
        }, 2000)
      } else {
        this.showNotification('Some updates failed. Please check the form and try again.', 'error')
        submitButton.textContent = originalText
        submitButton.disabled = false
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.showNotification('An error occurred while saving changes.', 'error')
      submitButton.textContent = originalText
      submitButton.disabled = false
    })
  }

  private updateSubmitButtonState() {
    const form = this.formTarget as HTMLFormElement
    if (!form) return

    const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement
    if (!submitButton) return

    // Check if we're in edit_all mode (multi-language table) or single-language mode
    const isEditAllMode = this.languageColumnTargets.length > 0

    if (isEditAllMode) {
      // Multi-language mode: check for visible columns
      const visibleColumns = this.languageColumnTargets.filter((column: HTMLElement) => 
        column.style.display !== 'none'
      )
      const hasVisibleLanguages = visibleColumns.length > 0

      submitButton.disabled = !hasVisibleLanguages
      submitButton.className = hasVisibleLanguages
        ? "px-6 py-2 bg-gradient-to-r from-violet-600 to-violet-500 text-white font-semibold rounded-lg hover:from-violet-700 hover:to-violet-600 transition-all duration-300 shadow-lg hover:shadow-xl"
        : "px-6 py-2 bg-gray-400 text-gray-200 font-semibold rounded-lg cursor-not-allowed"
      
      // Update button text
      if (!hasVisibleLanguages) {
        submitButton.textContent = "No Languages Selected"
      } else {
        submitButton.textContent = "Save All Changes"
      }
    } else {
      // Single-language mode: always enable submit
      submitButton.disabled = false
      submitButton.className = "px-6 py-2 bg-gradient-to-r from-violet-600 to-violet-500 text-white font-semibold rounded-lg hover:from-violet-700 hover:to-violet-600 transition-all duration-300 shadow-lg hover:shadow-xl"
      submitButton.textContent = "Save Changes"
    }
  }

  private showNotification(message: string, type: 'success' | 'error') {
    // Create notification element
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    } text-white font-medium`
    notification.textContent = message

    document.body.appendChild(notification)

    // Remove after 3 seconds
    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}